import type { Category, CategoryKey } from '../types';

const imageCategory = (key: CategoryKey, title: string, files: string[]): Category => ({
  key,
  title,
  works: files.map((url) => ({ kind: 'image', url })),
});

export const categories: Record<CategoryKey, Category> = {
  app: {
    key: 'app',
    title: 'Apps',
    works: [
      {
        kind: 'app',
        name: 'Muvic',
        description:
          'A desktop app that syncs your Spotify playlists to your Beatport cart. Built with Electron and React, it uses Puppeteer to automate Beatport and the Spotify Web API to pull liked tracks, so DJs can quickly add new finds to their collection.',
        technologies: [
          '/assets/technologies/typescript-icon.png',
          '/assets/technologies/react-icon-2.png',
          '/assets/technologies/electron-icon.png',
          '/assets/technologies/node-icon.png',
          '/assets/technologies/express-icon.png',
          '/assets/technologies/sqlite-icon.png',
          '/assets/technologies/docker-icon.png',
          '/assets/technologies/puppeteer-icon.png',
          '/assets/technologies/spotify-icon.png',
          '/assets/technologies/jest-icon.png',
          '/assets/technologies/playwright-icon.png',
        ],
        url: '/assets/gifs/muvic.webp',
        site: 'https://muvic.app',
        hideVideo: true,
      },
      {
        kind: 'app',
        name: 'Fridge Vision',
        description:
          'A Native app that allows users to capture a photo of their fridge or pantry with their mobile device. Fridge Vision then uses an image recognition API to create a list of ingredients. Users are then presented with recipes that they can make with the available food items they already have.',
        technologies: [
          '/assets/technologies/react-native-logo.png',
          '/assets/technologies/express-icon.png',
          '/assets/technologies/node-icon.png',
          '/assets/technologies/aws-icon.png',
          '/assets/technologies/postgresql-icon.png',
          '/assets/technologies/jest-icon.png',
          '/assets/technologies/knex-icon.png',
        ],
        url: '/assets/gifs/fridge-vision.gif',
        code: 'https://github.com/SethCaparelli/fridge-vision',
      },
      {
        kind: 'app',
        name: 'Music@',
        description:
          'Oauth2 connects users to their Spotify account and populates the page with their followed artists. With a click of a button, users are able to bring up the tour information from the Bands In Town Api for their favorite artists',
        technologies: [
          '/assets/technologies/react-icon-2.png',
          '/assets/technologies/html-logo.png',
          '/assets/technologies/css-icon.png',
          '/assets/technologies/bootstrap-icon.png',
          '/assets/technologies/node-icon.png',
          '/assets/technologies/cypress-icon.png',
        ],
        url: '/assets/gifs/music.gif',
        code: 'https://github.com/SethCaparelli/music-app-frontend',
      },
      {
        kind: 'app',
        name: 'Cocktail=>',
        description:
          'An app that allows users to search for cocktails based off name, ingredient, or random. It then prompts users to search for the corresponding recipe.',
        technologies: [
          '/assets/technologies/react-icon-2.png',
          '/assets/technologies/html-logo.png',
          '/assets/technologies/css-icon.png',
          '/assets/technologies/bootstrap-icon.png',
          '/assets/technologies/cypress-icon.png',
        ],
        url: '/assets/gifs/cocktail.gif',
        code: 'https://github.com/SethCaparelli/cocktail-app-react',
      },
    ],
  },
  cactus: imageCategory('cactus', 'Cactus^3', [
    '/assets/cactus/cactus^3_ver_1.jpg',
    '/assets/cactus/cactus^3_ver_1_close_1.jpg',
    '/assets/cactus/cactus^3_ver_1_close_2.jpg',
    '/assets/cactus/cactus^3_ver_2.jpg',
    '/assets/cactus/cactus^3_ver_2_close.jpg',
    '/assets/cactus/cactus^3_ver_3.jpg',
    '/assets/cactus/cactus^3_ver_3_close.jpg',
    '/assets/cactus/cactus^3_ver_4.jpg',
    '/assets/cactus/cactus^3_ver_5.jpg',
    '/assets/cactus/cactus^3_ver_5_close.jpg',
    '/assets/cactus/cactus^3_ver_6.jpg',
    '/assets/cactus/cactus^3_ver_6_close.jpg',
    '/assets/cactus/cactus^3_ver_7.jpg',
    '/assets/cactus/cactus^3_ver_7_close.jpg',
  ]),
  tree: imageCategory('tree', 'Reclaim Tree', [
    '/assets/tree/reclaim_tree_ver_1.jpg',
    '/assets/tree/reclaim_tree_ver_2.jpg',
    '/assets/tree/reclaim_tree_ver_2_close_1.jpg',
    '/assets/tree/reclaim_tree_ver_2_close_2.jpg',
    '/assets/tree/reclaim_tree_ver_3.jpg',
    '/assets/tree/reclaim_tree_ver_3_close.jpg',
  ]),
  sculpture: imageCategory('sculpture', 'Sculpture', [
    '/assets/sculpture/struggling_pose.jpg',
    '/assets/sculpture/head_front.jpg',
    '/assets/sculpture/head_side.jpg',
    '/assets/sculpture/head_right.jpg',
    '/assets/sculpture/head_left.jpg',
    '/assets/sculpture/head_back.jpg',
    '/assets/sculpture/sky^3.jpg',
    '/assets/sculpture/sky^3_close.jpg',
    '/assets/sculpture/stone_hammers.jpg',
    '/assets/sculpture/stone_hammers_close.jpg',
    '/assets/sculpture/ring_and_container.jpg',
    '/assets/sculpture/ring_and_container_close.jpg',
    '/assets/sculpture/gear_postcard_front.jpg',
    '/assets/sculpture/gear_postcard_back.jpg',
  ]),
  vessel: imageCategory('vessel', 'Vessel', [
    '/assets/vessel/tea_pot_1.jpg',
    '/assets/vessel/tea_pot_2.jpg',
    '/assets/vessel/tea_pot_3.jpg',
    '/assets/vessel/tea_pot_4.jpg',
    '/assets/vessel/pitcher_1.jpg',
    '/assets/vessel/pitcher_2.jpg',
    '/assets/vessel/cup_5.jpg',
    '/assets/vessel/cup_6.jpg',
    '/assets/vessel/cup_1.jpg',
    '/assets/vessel/cup_2.jpg',
    '/assets/vessel/cup_3.jpg',
    '/assets/vessel/cup_4.jpg',
    '/assets/vessel/plate_1.jpg',
    '/assets/vessel/plate_2.jpg',
    '/assets/vessel/plate_3.jpg',
    '/assets/vessel/plate_4.jpg',
    '/assets/vessel/watering_can_1.jpg',
    '/assets/vessel/puzzle_plate_1.jpg',
    '/assets/vessel/puzzle_plate_2.jpg',
  ]),
  painting: imageCategory('painting', 'Painting', [
    '/assets/painting/leaving_las_vegas.jpg',
    '/assets/painting/the_future^3_installation.jpg',
    '/assets/painting/the_future^3.jpg',
    '/assets/painting/the_future^3_close.jpg',
    '/assets/painting/tomato_soup.jpg',
    '/assets/painting/happy_tree.jpg',
    '/assets/painting/sad_tree.jpg',
    '/assets/painting/me_jean.jpg',
  ]),
  advertising: imageCategory('advertising', 'Advertising', [
    '/assets/advertising/etch_a_sketch_1.jpg',
    '/assets/advertising/etch_a_sketch_2.jpg',
    '/assets/advertising/etch_a_sketch_3.jpg',
    '/assets/advertising/shout_wine.jpg',
    '/assets/advertising/shout_mustard.jpg',
    '/assets/advertising/shout_grass.jpg',
    '/assets/advertising/vans_ur_print_cover.jpg',
    '/assets/advertising/vans_ur_print_1.jpg',
    '/assets/advertising/vans_ur_print_2.jpg',
    '/assets/advertising/vans_ur_print_3.jpg',
    '/assets/advertising/gphone_1.jpg',
    '/assets/advertising/gphone_2.jpg',
    '/assets/advertising/gphone_3.jpg',
    '/assets/advertising/best_if_used_by_1.jpg',
    '/assets/advertising/best_if_used_by_2.jpg',
    '/assets/advertising/best_if_used_by_3.jpg',
  ]),
  design: imageCategory('design', 'Design', [
    '/assets/graphic-design/surrealism_poster.jpg',
    '/assets/graphic-design/nourish_1.jpg',
    '/assets/graphic-design/nourish_de_muertos.jpg',
    '/assets/graphic-design/spirit_thing.jpg',
    '/assets/graphic-design/next_thing.jpg',
    '/assets/graphic-design/lost_bulldog_poster.jpg',
    '/assets/graphic-design/bobble_logo.jpg',
    '/assets/graphic-design/elm_and_oak_shirt_1.jpg',
    '/assets/graphic-design/elm_and_oak_shirt_2.jpg',
    '/assets/graphic-design/elm_and_oak_shirt_3.jpg',
    '/assets/graphic-design/elm_and_oak_shirt_4.jpg',
  ]),
};

export const categoryOrder: CategoryKey[] = [
  'app',
  'cactus',
  'tree',
  'sculpture',
  'vessel',
  'painting',
  'advertising',
  'design',
];
